import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { doc, setDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { CartContextType, CartItem, Product } from '../types';
import { useAuth } from './useAuth';

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { user } = useAuth();

  // Load cart from Firebase when user logs in
  useEffect(() => {
    if (!user) {
      // If no user, we might want to keep local items or clear them. 
      // For now, let's keep local items until they login/logout explicitly clears.
      // But if user logs out, usually we clear cart or keep it local.
      return; 
    }

    const cartRef = doc(db, "users", user.email);
    const unsubscribe = onSnapshot(cartRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        if (data.cart) {
          setItems(data.cart);
        }
      }
    });

    return () => unsubscribe();
  }, [user]);

  // Sync cart to Firebase whenever it changes (if user is logged in)
  const syncCartToFirebase = async (newItems: CartItem[]) => {
    if (!user) return;
    try {
      const userRef = doc(db, "users", user.email);
      await setDoc(userRef, { cart: newItems }, { merge: true });
    } catch (error) {
      console.error("Error syncing cart:", error);
    }
  };

  const addToCart = (product: Product, options?: { selectedVariants?: CartItem['selectedVariants']; unitPrice?: number }) => {
    let newItems;

    const signature = JSON.stringify(options?.selectedVariants || []);
    const existing = items.find(item => {
      const itemSignature = JSON.stringify(item.selectedVariants || []);
      return item.id === product.id && itemSignature === signature;
    });
    
    if (existing) {
      newItems = items.map(item => 
        item === existing 
          ? { ...item, quantity: item.quantity + 1 } 
          : item
      );
    } else {
      newItems = [...items, { ...product, quantity: 1, selectedVariants: options?.selectedVariants || [], unitPrice: options?.unitPrice ?? product.currentPrice }];
    }
    
    setItems(newItems);
    syncCartToFirebase(newItems);
    setIsCartOpen(true);
  };

  const removeFromCart = (productId: string, selectedVariantsSignature?: string) => {
    const newItems = items.filter(item => {
      if (item.id !== productId) return true;
      if (!selectedVariantsSignature) return false;
      const itemSignature = JSON.stringify(item.selectedVariants || []);
      return itemSignature !== selectedVariantsSignature;
    });
    setItems(newItems);
    syncCartToFirebase(newItems);
  };

  const updateQuantity = (productId: string, quantity: number, selectedVariantsSignature?: string) => {
    if (quantity === 0) {
      removeFromCart(productId, selectedVariantsSignature);
      return;
    }
    const newItems = items.map(item => {
      if (item.id !== productId) return item;
      if (!selectedVariantsSignature) return { ...item, quantity };
      const itemSignature = JSON.stringify(item.selectedVariants || []);
      if (itemSignature !== selectedVariantsSignature) return item;
      return { ...item, quantity };
    });
    setItems(newItems);
    syncCartToFirebase(newItems);
  };

  const clearCart = () => {
    setItems([]);
    syncCartToFirebase([]);
  };

  const cartTotal = useMemo(() => {
    return items.reduce((total, item) => {
      const unit = typeof item.unitPrice === 'number' && Number.isFinite(item.unitPrice) ? item.unitPrice : item.currentPrice;
      return total + (unit * item.quantity);
    }, 0);
  }, [items]);

  const cartCount = useMemo(() => {
    return items.reduce((count, item) => count + item.quantity, 0);
  }, [items]);

  return (
    <CartContext.Provider value={{
      items,
      addToCart,
      removeFromCart,
      updateQuantity,
      isCartOpen,
      setIsCartOpen,
      clearCart,
      cartTotal,
      cartCount
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};